import { useState, useEffect, useRef } from 'react';
import { Search, GripVertical, Trash2, Plus, ExternalLink } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiCall } from './useAutoSave';
import { fmtCena } from './receptUtils';
import TypBadge from './TypBadge';

const ACCENT = '#EF9F27';

function getItemTyp(item) {
    if (item.surovina_id) return 'MAT';
    if (item.ingredience_recept_id) return 'SEM';
    if (item.obal_id) return 'OBA';
    if (item.spotrebni_zbozi_id) return 'SPOZ';
    return 'MAT';
}

function getItemLabel(item) {
    return item.surovina?.nazev || item.ingredience_recept?.nazev || item.obal?.nazev || item.spotrebni_zbozi?.nazev || item._label || '?';
}

function getItemKod(item) {
    return item.surovina?.kod || item.ingredience_recept?.kod || item.obal?.kod || item.spotrebni_zbozi?.kod || item._kod || '';
}

// Kam vede karta položky: suroviny a polotovary do Receptáře, obaly
// a provozní materiál do Balírny.
function cilPolozky(item) {
    if (item.surovina_id)           return ['spizirna', { polozka: { typ: 'MAT', id: item.surovina_id } }];
    if (item.obal_id)               return ['balirna', { polozka: { typ: 'OBA', id: item.obal_id } }];
    if (item.spotrebni_zbozi_id)    return ['balirna', { polozka: { typ: 'SPOZ', id: item.spotrebni_zbozi_id } }];
    if (item.ingredience_recept_id) return ['produkt-kvalita', { polozka: { typ: 'SEM', id: item.ingredience_recept_id } }];
    return null;
}

// Alergeny drží jen surovina (MAT); polotovar je nemá vlastní, plynou z jeho
// kusovníku a server je dopočítává do `ingredience_recept.alergeny`. Obaly a
// spotřební zboží alergeny nenesou vůbec.
// `_alergeny` je čerstvě vybraná položka z vyhledávání — draft řádek ještě
// žádnou relaci nemá, takže by do uložení zůstal bez značek.
function getItemAlergeny(item) {
    return item.surovina?.alergeny
        || item.ingredience_recept?.alergeny
        || item._alergeny
        || [];
}

// Kompaktní značky alergenů za názvem — číslo dle EU (1–14), plný název v tooltipu.
function AlergenBadges({ alergeny }) {
    if (!alergeny || alergeny.length === 0) return null;
    return (
        <span className="flex items-center gap-0.5 flex-shrink-0">
            {alergeny.map(a => (
                <span key={a.id}
                    title={`${a.ikona ? a.ikona + ' ' : ''}${a.cislo_eu}. ${a.nazev}`}
                    className="inline-flex items-center justify-center rounded"
                    style={{
                        fontSize: 9, fontWeight: 700, lineHeight: 1,
                        minWidth: 15, height: 15, padding: '0 3px',
                        background: 'rgba(239,68,68,0.12)', color: '#EF4444', cursor: 'help',
                    }}>
                    {a.cislo_eu}
                </span>
            ))}
        </span>
    );
}

function getAvailableUnits(item, jednotky) {
    const baseJednotkaId = item.surovina?.jednotka_id || item.obal?.jednotka_id || item.spotrebni_zbozi?.jednotka_id || item.ingredience_recept?.jednotka_id;
    const altJednotky = item.surovina?.alt_jednotky || item.obal?.alt_jednotky || item.spotrebni_zbozi?.alt_jednotky || item.ingredience_recept?.alt_jednotky || [];
    const ids = new Set([baseJednotkaId, ...altJednotky.map(a => a.jednotka_z_id), ...altJednotky.map(a => a.jednotka_do_id)].filter(Boolean));
    return jednotky.filter(j => ids.has(j.id));
}

function SortableIngredientRow({ item, isDark, jednotky, cost, materialTotal, pending, onUpdate, onDelete, onNavigate }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)';

    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 5,
        padding: '4px 8px', fontSize: 12, outline: 'none',
        colorScheme: isDark ? 'dark' : 'light',
    };
    const optionStyle = { background: isDark ? '#1e1e2e' : '#fff', color: isDark ? '#e2e8f0' : '#1e293b' };

    const availableUnits = getAvailableUnits(item, jednotky);
    const cil = onNavigate ? cilPolozky(item) : null;

    return (
        <div ref={setNodeRef} style={style} {...attributes}
            className="flex items-center gap-2 py-1.5 px-1 rounded-lg"
            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {/* Drag handle */}
            <div ref={setActivatorNodeRef} {...listeners} style={{ cursor: 'grab', color: mutedColor, flexShrink: 0 }}>
                <GripVertical size={14} />
            </div>

            {/* Type badge */}
            <TypBadge typ={getItemTyp(item)} />

            {/* Name + allergens + code — název vede na kartu položky */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    {cil ? (
                        <button
                            onClick={() => onNavigate(cil[0], cil[1])}
                            title={`Otevřít kartu — ${getItemLabel(item)}`}
                            className="text-xs font-medium truncate group"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', maxWidth: '100%' }}
                            onMouseEnter={e => { e.currentTarget.style.color = ACCENT; e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.textDecoration = 'none'; }}>
                            {getItemLabel(item)}
                            <ExternalLink size={9} style={{ display: 'inline', marginLeft: 4, verticalAlign: 'baseline', opacity: 0.45 }} />
                        </button>
                    ) : (
                        <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{getItemLabel(item)}</span>
                    )}
                    <AlergenBadges alergeny={getItemAlergeny(item)} />
                </div>
                <div className="text-[10px] truncate" style={{ color: mutedColor }}>{getItemKod(item)}</div>
            </div>

            {/* Quantity */}
            <input type="number" min="0" step="any"
                value={item.spotreba || 0}
                onChange={e => onUpdate(item.id, { spotreba: parseFloat(e.target.value) || 0 })}
                style={{ ...inputStyle, width: 70, textAlign: 'center' }} />

            {/* Unit */}
            <select value={item.jednotka_id || ''}
                onChange={e => onUpdate(item.id, { jednotka_id: parseInt(e.target.value) })}
                style={{ ...inputStyle, cursor: 'pointer', width: 65 }}>
                {(availableUnits.length > 0 ? availableUnits : jednotky).map(j => (
                    <option key={j.id} value={j.id} style={optionStyle}>{j.zkratka}</option>
                ))}
            </select>

            {/* Odpad — kolik % suroviny se při zpracování ztratí. Vstupuje do
                kalkulace jako samostatná kategorie, ale zadat ho nešlo nikde. */}
            {(() => {
                const odpad = Number(item.scrap_pct) || 0;
                return (
                    <div className="flex items-center" style={{ flexShrink: 0 }}
                        title={`Odpad ${odpad} % — kolik se ho při zpracování ztratí (očištění, odkrojení). Připočítá se k nákladu položky.`}>
                        <input type="number" min="0" max="100" step="any"
                            value={odpad}
                            onChange={e => onUpdate(item.id, { scrap_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                            style={{
                                ...inputStyle, width: 52, textAlign: 'center', paddingRight: 4,
                                borderColor: odpad > 0 ? 'rgba(239,68,68,0.45)' : undefined,   // jinak platí rámeček z inputStyle
                                color: odpad > 0 ? '#EF4444' : 'var(--text-primary)',
                            }} />
                        <span className="text-[10px]" style={{ color: mutedColor, marginLeft: 2 }}>%</span>
                    </div>
                );
            })()}

            {/* Náklad za vložené množství (na celou dávku) */}
            <div style={{ width: 96, flexShrink: 0, textAlign: 'right', opacity: pending ? 0.4 : 1, transition: 'opacity 0.15s' }}
                title={cost?.cena_chybi
                    ? 'Materiál nemá platnou cenu — do kalkulace vstupuje nulou.'
                    : (cost
                        ? `Náklad za ${item.spotreba || 0} zadaných jednotek, podíl na materiálu receptu`
                            + (Number(item.scrap_pct) > 0 ? ` · odpad ${item.scrap_pct} % připočítá dalších ${fmtCena(Number(cost.cost) * Number(item.scrap_pct) / 100)}` : '')
                        : 'Náklad se dopočítá')}>
                {cost?.cena_chybi ? (
                    <span className="text-[11px] font-medium" style={{ color: '#EF4444' }}>chybí cena</span>
                ) : cost ? (
                    <>
                        <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtCena(cost.cost)}</div>
                        <div className="text-[10px]" style={{ color: mutedColor }}>
                            {materialTotal > 0 ? `${Math.round((Number(cost.cost) / materialTotal) * 100)} %` : '—'}
                        </div>
                    </>
                ) : (
                    <span className="text-xs" style={{ color: mutedColor }}>—</span>
                )}
            </div>

            {/* Delete */}
            <button onClick={() => onDelete(item.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, flexShrink: 0 }}
                onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                onMouseLeave={e => e.currentTarget.style.color = mutedColor}>
                <Trash2 size={13} />
            </button>
        </div>
    );
}

export default function StepIngredience({ step, stepBom, isDark, jednotky, itemCosts, materialTotal = 0, costsPending, jednotkaZkratka, davkaMnozstvi, souhrnLabel = 'Materiál kroku', skrytHledani = false, onNavigate, onAddBom, onUpdateBom, onDeleteBom, onReorderBom }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)';

    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6,
        padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none',
        colorScheme: isDark ? 'dark' : 'light',
    };

    // Search debounce
    useEffect(() => {
        if (searchQuery.length < 1) { setSearchResults([]); return; }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await apiCall(`/api/recepty/materialy/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(results);
                setShowResults(true);
            } catch { setSearchResults([]); }
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchQuery]);

    // Close dropdown on click outside
    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Add ingredient — jen do draftu (uloží se přes Uložit)
    const addIngredient = (item) => {
        setSearchQuery('');
        setShowResults(false);
        setSearchResults([]);
        onAddBom?.(item, step.id);
    };

    // Update ingredient field — jen do draftu
    const updateIngredient = (itemId, updates) => onUpdateBom?.(itemId, updates);

    // Delete ingredient — jen do draftu
    const deleteIngredient = (itemId) => onDeleteBom?.(itemId);

    // Náklad kroku = součet položek kroku (na celou dávku)
    const krokMaterial = stepBom.reduce((sum, b) => sum + (Number(itemCosts?.get(String(b.id))?.cost) || 0), 0);
    const krokOdpad = stepBom.reduce((sum, b) => sum + (Number(itemCosts?.get(String(b.id))?.cost) || 0) * (Number(b.scrap_pct) || 0) / 100, 0);
    const chybiCena = stepBom.some(b => itemCosts?.get(String(b.id))?.cena_chybi);

    // Reorder — jen do draftu
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = stepBom.findIndex(b => b.id === active.id);
        const newIndex = stepBom.findIndex(b => b.id === over.id);
        const reordered = arrayMove(stepBom, oldIndex, newIndex);
        onReorderBom?.(step.id, reordered.map(b => b.id));
    };

    return (
        <div>
            {/* Search bar */}
            <div ref={searchRef} className="relative mb-3" style={{ display: skrytHledani ? 'none' : undefined }}>
                <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: mutedColor }} />
                    <input
                        placeholder="Hledat v knihovne materialu (MAT, SEM, OBA)..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
                        style={{ ...inputStyle, paddingLeft: 32 }}
                    />
                </div>

                {/* Results dropdown */}
                {showResults && searchResults.length > 0 && (
                    <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg overflow-hidden"
                        style={{
                            background: isDark ? '#1e1e2e' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            maxHeight: 280, overflowY: 'auto',
                        }}>
                        {searchResults.map(item => (
                            <button key={`${item.typ}-${item.id}`}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13 }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                onClick={() => addIngredient(item)}>
                                <TypBadge typ={item.typ} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="font-medium truncate">{item.nazev}</span>
                                        {/* Alergeny už tady, ať je vidět dřív než po vybrání */}
                                        <AlergenBadges alergeny={item.alergeny} />
                                    </div>
                                    <div className="text-[11px]" style={{ color: secondaryColor }}>{item.kod}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Hlavička sloupců — pevné šířky sedí s řádkem, jméno bere zbytek */}
            {stepBom.length > 0 && (
                <div className="flex items-center gap-2 px-1 pb-1" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: mutedColor, fontWeight: 600 }}>
                    <div style={{ width: 14, flexShrink: 0 }} />
                    <div className="flex-1 min-w-0">Ingredience</div>
                    <div style={{ width: 70, flexShrink: 0, textAlign: 'center' }}>Množství</div>
                    <div style={{ width: 65, flexShrink: 0, textAlign: 'center' }}>Jedn.</div>
                    <div style={{ width: 63, flexShrink: 0, textAlign: 'center' }}>Odpad</div>
                    <div style={{ width: 96, flexShrink: 0, textAlign: 'right' }}>Náklad</div>
                    <div style={{ width: 17, flexShrink: 0 }} />
                </div>
            )}

            {/* Ingredient list */}
            {stepBom.length > 0 ? (
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <SortableContext items={stepBom.map(b => b.id)} strategy={verticalListSortingStrategy}>
                        {stepBom.map(item => (
                            <SortableIngredientRow
                                key={item.id}
                                item={item}
                                isDark={isDark}
                                jednotky={jednotky}
                                cost={itemCosts?.get(String(item.id))}
                                materialTotal={materialTotal}
                                pending={costsPending}
                                onNavigate={onNavigate}
                                onUpdate={updateIngredient}
                                onDelete={deleteIngredient}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ) : (
                <div className="text-center py-6 text-xs" style={{ color: mutedColor }}>
                    V tomto kroku nejsou zadne ingredience.
                </div>
            )}

            {/* Součet materiálu kroku — stejná základna jako sloupec nákladů (celá dávka) */}
            {stepBom.length > 0 && (
                <div className="flex items-center justify-between mt-2 pt-2"
                    style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`, opacity: costsPending ? 0.4 : 1, transition: 'opacity 0.15s' }}>
                    <span className="text-[11px]" style={{ color: secondaryColor }}>
                        {souhrnLabel}{davkaMnozstvi ? ` — za dávku ${davkaMnozstvi} ${jednotkaZkratka || ''}`.trimEnd() : ''}
                        {krokOdpad > 0 && <span style={{ color: '#EF4444' }}> · odpad {fmtCena(krokOdpad)}</span>}
                        {chybiCena && <span style={{ color: '#EF4444' }}> · některé položky nemají cenu</span>}
                    </span>
                    <span className="text-xs font-bold" style={{ color: 'var(--text-primary)', paddingRight: 25 }}>
                        {fmtCena(krokMaterial)}
                        {materialTotal > 0 && (
                            <span className="font-normal ml-1.5" style={{ color: mutedColor }}>
                                {Math.round((krokMaterial / materialTotal) * 100)} % receptu
                            </span>
                        )}
                    </span>
                </div>
            )}
        </div>
    );
}
